import React from 'react'

interface IconProps {
  name: string // The name of the icon to render
  className?: string // Additional class names for styling
  strokeWidth?: number // Optional stroke width for applicable icons
}

// Define all icons as an object
const icons: Record<string, React.FC<{ className?: string; strokeWidth?: number }>> = {
  close: ({ className, strokeWidth }) => (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      aria-label="close"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1="4.35355" y1="4.64645" x2="16.3536" y2="16.6464" stroke="currentColor" strokeWidth={strokeWidth || 2} />
      <path d="M4.50031 17L15.6329 4.66092" stroke="currentColor" strokeWidth={strokeWidth || 2} />
    </svg>
  ),
  minus: ({ className, strokeWidth }) => (
    <svg
      className={className}
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1="2.40002" y1="8.1" x2="13.6" y2="8.1" stroke="currentColor" strokeWidth={strokeWidth || 0.8} />
    </svg>
  ),
  question: ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 20 20">
      <path
        d="M9.99998 4C8.95702 4.00132 7.95716 4.41622 7.21968 5.1537C6.4822 5.89118 6.0673 6.89104 6.06598 7.934H8.06598C8.06598 6.867 8.93398 6 9.99998 6C11.066 6 11.934 6.867 11.934 7.934C11.934 8.532 11.453 8.966 10.718 9.56C10.4779 9.7481 10.2472 9.94801 10.027 10.159C9.02898 11.156 8.99998 12.215 8.99998 12.333V13H11L10.999 12.367C11 12.351 11.032 11.981 11.44 11.574C11.59 11.424 11.779 11.274 11.975 11.116C12.754 10.485 13.933 9.532 13.933 7.934C13.9322 6.89106 13.5176 5.89104 12.7802 5.15347C12.0428 4.4159 11.0429 4.00106 9.99998 4ZM8.99998 14H11V16H8.99998V14Z"
        fill={className}
      />
      <path
        d="M10 0C4.486 0 0 4.486 0 10C0 15.514 4.486 20 10 20C15.514 20 20 15.514 20 10C20 4.486 15.514 0 10 0ZM10 18C5.589 18 2 14.411 2 10C2 5.589 5.589 2 10 2C14.411 2 18 5.589 18 10C18 14.411 14.411 18 10 18Z"
        fill={className}
      />
    </svg>
  ),
  plus: ({ className, strokeWidth }) => (
    <svg
      className={className}
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1="2.40002" y1="8.1" x2="13.6" y2="8.1" stroke="currentColor" strokeWidth={strokeWidth || 0.8} />
      <line x1="8.4" y1="2.90002" x2="8.4" y2="14.1" stroke="currentColor" strokeWidth={strokeWidth || 0.8} />
    </svg>
  ),
  cart: ({ className }) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
    >
      <g filter="url(#filter0_d_8419_2688)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.8702 0.989582C12.9143 0.551401 13.2831 0.217829 13.7235 0.217829H16.4828C16.9565 0.217829 17.3404 0.601777 17.3404 1.0754C17.3404 1.54903 16.9565 1.93298 16.4828 1.93298H14.4991L14.3068 3.69498C14.3056 3.73742 14.3014 3.7791 14.2942 3.81978L13.7289 9.44095C13.7274 9.45524 13.7256 9.4695 13.7235 9.48371C13.6526 9.95139 13.415 10.3777 13.0546 10.684C12.6956 10.9892 12.2388 11.1549 11.7678 11.1509H4.83745C4.41847 11.1673 4.00501 11.049 3.65791 10.8129C3.30375 10.5721 3.03816 10.222 2.90169 9.81602L2.90079 9.81334L1.43282 5.39837L1.43215 5.39635C1.33489 5.1014 1.30907 4.78757 1.3568 4.48068C1.40433 4.17516 1.52341 3.88523 1.70431 3.63452C1.89392 3.36871 2.1469 3.15442 2.44033 3.01112C2.72843 2.87042 3.04654 2.80254 3.36676 2.81328H12.6717L12.8702 0.989582ZM12.4992 4.52843H3.35033C3.33755 4.52843 3.32478 4.52814 3.31202 4.52757C3.2709 4.52574 3.22998 4.53424 3.19299 4.5523C3.15601 4.57036 3.12414 4.59741 3.10031 4.63097L3.09552 4.63771C3.07265 4.66928 3.05756 4.7058 3.05157 4.74431C3.04561 4.7826 3.04878 4.82175 3.06081 4.85858L4.52744 9.2695C4.54465 9.32009 4.57815 9.36458 4.62235 9.39463C4.6668 9.42486 4.71992 9.4397 4.7736 9.4369C4.78849 9.43612 4.8034 9.43573 4.8183 9.43573H11.7816C11.8409 9.43641 11.8985 9.41565 11.9438 9.3772C11.9855 9.34174 12.0141 9.29349 12.0253 9.24016L12.4992 4.52843Z"
          fill="currentColor"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.59736 13.0002C5.43948 13.0002 5.3115 13.1282 5.3115 13.2861C5.3115 13.4439 5.43948 13.5719 5.59736 13.5719C5.75523 13.5719 5.88321 13.4439 5.88321 13.2861C5.88321 13.1282 5.75523 13.0002 5.59736 13.0002ZM3.59635 13.2861C3.59635 12.1809 4.49223 11.2851 5.59736 11.2851C6.70248 11.2851 7.59836 12.1809 7.59836 13.2861C7.59836 14.3912 6.70248 15.2871 5.59736 15.2871C4.49223 15.2871 3.59635 14.3912 3.59635 13.2861Z"
          fill="currentColor"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.5443 13.0002C11.3865 13.0002 11.2585 13.1282 11.2585 13.2861C11.2585 13.4439 11.3865 13.5719 11.5443 13.5719C11.7022 13.5719 11.8302 13.4439 11.8302 13.2861C11.8302 13.1282 11.7022 13.0002 11.5443 13.0002ZM9.54334 13.2861C9.54334 12.1809 10.4392 11.2851 11.5443 11.2851C12.6495 11.2851 13.5453 12.1809 13.5453 13.2861C13.5453 14.3912 12.6495 15.2871 11.5443 15.2871C10.4392 15.2871 9.54334 14.3912 9.54334 13.2861Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_8419_2688"
          x="0.333496"
          y="0.217834"
          width="18.0068"
          height="17.0692"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="0.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_8419_2688" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_8419_2688" result="shape" />
        </filter>
      </defs>
    </svg>
  ),
  search: ({ className }) => (
    <svg
      className={className}
      width="19"
      height="19"
      viewBox="0 0 19 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 16C9.77498 15.9996 11.4988 15.4054 12.897 14.312L17.293 18.708L18.707 17.294L14.311 12.898C15.405 11.4997 15.9996 9.77544 16 8C16 3.589 12.411 0 8 0C3.589 0 0 3.589 0 8C0 12.411 3.589 16 8 16ZM8 2C11.309 2 14 4.691 14 8C14 11.309 11.309 14 8 14C4.691 14 2 11.309 2 8C2 4.691 4.691 2 8 2Z"
        fill="currentColor"
      />
    </svg>
  ),
  heart: ({ className }) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
    >
      <path
        d="M21.1736 4.60999C20.6629 4.099 20.0564 3.69364 19.389 3.41708C18.7215 3.14052 18.0061 2.99817 17.2836 2.99817C16.5611 2.99817 15.8457 3.14052 15.1783 3.41708C14.5108 3.69364 13.9044 4.099 13.3936 4.60999L12.3336 5.66999L11.2736 4.60999C10.2419 3.5783 8.84265 2.9987 7.38362 2.9987C5.92458 2.9987 4.52531 3.5783 3.49362 4.60999C2.46192 5.64169 1.88232 7.04096 1.88232 8.49999C1.88232 9.95903 2.46192 11.3583 3.49362 12.39L4.55361 13.45L12.3336 21.23L20.1136 13.45L21.1736 12.39C21.6846 11.8792 22.09 11.2728 22.3665 10.6053C22.6431 9.93789 22.7854 9.22248 22.7854 8.49999C22.7854 7.77751 22.6431 7.0621 22.3665 6.39464C22.09 5.72718 21.6846 5.12075 21.1736 4.60999Z"
        fill="currentColor"
      />
    </svg>
  ),
  filters: ({ className }) => (
    <svg
      className={className}
      width="16"
      height="10"
      viewBox="0 0 16 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M3 4H13V6H3V4ZM0 0H16V2H0V0ZM6 8H10V10H6V8Z" fill="currentColor" />
    </svg>
  ),
  trash: ({ className }) => (
    <svg
      className={className}
      width="12"
      height="14"
      viewBox="0 0 12 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.8 2.71951H7.2C7.2 2.38314 7.07357 2.06054 6.84853 1.82269C6.62348 1.58484 6.31826 1.45122 6 1.45122C5.68174 1.45122 5.37652 1.58484 5.15147 1.82269C4.92643 2.06054 4.8 2.38314 4.8 2.71951ZM3.9 2.71951C3.9 2.42804 3.95432 2.13943 4.05985 1.87014C4.16539 1.60086 4.32007 1.35618 4.51508 1.15008C4.71008 0.943979 4.94158 0.780491 5.19637 0.66895C5.45115 0.557409 5.72422 0.5 6 0.5C6.27578 0.5 6.54885 0.557409 6.80364 0.66895C7.05842 0.780491 7.28992 0.943979 7.48492 1.15008C7.67993 1.35618 7.83461 1.60086 7.94015 1.87014C8.04568 2.13943 8.1 2.42804 8.1 2.71951H11.55C11.6693 2.71951 11.7838 2.76962 11.8682 2.85881C11.9526 2.94801 12 3.06898 12 3.19512C12 3.32126 11.9526 3.44223 11.8682 3.53143C11.7838 3.62062 11.6693 3.67073 11.55 3.67073H10.758L10.056 11.3509C10.0022 11.9394 9.74283 12.4855 9.32861 12.8829C8.91439 13.2802 8.37499 13.5002 7.8156 13.5H4.1844C3.62512 13.5001 3.08586 13.28 2.67177 12.8827C2.25768 12.4853 1.99844 11.9392 1.9446 11.3509L1.242 3.67073H0.45C0.330653 3.67073 0.216193 3.62062 0.131802 3.53143C0.0474105 3.44223 0 3.32126 0 3.19512C0 3.06898 0.0474105 2.94801 0.131802 2.85881C0.216193 2.76962 0.330653 2.71951 0.45 2.71951H3.9ZM5.1 5.73171C5.1 5.60557 5.05259 5.48459 4.9682 5.3954C4.88381 5.30621 4.76935 5.2561 4.65 5.2561C4.53065 5.2561 4.41619 5.30621 4.3318 5.3954C4.24741 5.48459 4.2 5.60557 4.2 5.73171V10.4878C4.2 10.6139 4.24741 10.7349 4.3318 10.8241C4.41619 10.9133 4.53065 10.9634 4.65 10.9634C4.76935 10.9634 4.88381 10.9133 4.9682 10.8241C5.05259 10.7349 5.1 10.6139 5.1 10.4878V5.73171ZM7.35 5.2561C7.46935 5.2561 7.58381 5.30621 7.6682 5.3954C7.75259 5.48459 7.8 5.60557 7.8 5.73171V10.4878C7.8 10.6139 7.75259 10.7349 7.6682 10.8241C7.58381 10.9133 7.46935 10.9634 7.35 10.9634C7.23065 10.9634 7.11619 10.9133 7.0318 10.8241C6.94741 10.7349 6.9 10.6139 6.9 10.4878V5.73171C6.9 5.60557 6.94741 5.48459 7.0318 5.3954C7.11619 5.30621 7.23065 5.2561 7.35 5.2561ZM2.8404 11.2596C2.87276 11.6126 3.02835 11.9402 3.27682 12.1785C3.5253 12.4169 3.84885 12.5489 4.1844 12.5488H7.8156C8.15115 12.5489 8.4747 12.4169 8.72318 12.1785C8.97165 11.9402 9.12724 11.6126 9.1596 11.2596L9.8544 3.67073H2.1456L2.8404 11.2596Z"
        fill="currentColor"
      />
    </svg>
  ),
  clock: ({ className }) => (
    <svg
      className={className}
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12.5 6V12L16.5 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  'free-shipping': ({ className }) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M16.2248 8.33946V9.00413C16.224 10.5621 15.7195 12.078 14.7866 13.3258C13.8538 14.5737 12.5426 15.4865 11.0486 15.9282C9.55457 16.37 7.95778 16.3169 6.49637 15.777C5.03496 15.2371 3.78723 14.2392 2.93927 12.9323C2.0913 11.6253 1.68854 10.0792 1.79105 8.52464C1.89356 6.97006 2.49585 5.49026 3.50808 4.30594C4.52032 3.12163 5.88827 2.29625 7.40792 1.9529C8.92758 1.60956 10.5175 1.76664 11.9406 2.40073"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.78 4.26056L8.11104 10.9362L6.11035 8.93551"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  'free-gift': ({ className }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
    >
      <g clipPath="url(#clip0_10090_36728)">
        <path
          d="M13.3332 8V14.6667H2.6665V8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.6668 4.66669H1.3335V8.00002H14.6668V4.66669Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 14.6667V4.66669"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8.00016 4.66665H5.00016C4.55814 4.66665 4.13421 4.49105 3.82165 4.17849C3.50909 3.86593 3.3335 3.44201 3.3335 2.99998C3.3335 2.55795 3.50909 2.13403 3.82165 1.82147C4.13421 1.50891 4.55814 1.33331 5.00016 1.33331C7.3335 1.33331 8.00016 4.66665 8.00016 4.66665Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 4.66665H11C11.442 4.66665 11.866 4.49105 12.1785 4.17849C12.4911 3.86593 12.6667 3.44201 12.6667 2.99998C12.6667 2.55795 12.4911 2.13403 12.1785 1.82147C11.866 1.50891 11.442 1.33331 11 1.33331C8.66667 1.33331 8 4.66665 8 4.66665Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_10090_36728">
          <rect width="16" height="16" rx="4" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),
  'chevron-down': ({ className }) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.32702 11.4714C8.1464 11.652 7.85361 11.652 7.67299 11.4714L2.95773 6.75614C2.77711 6.57552 2.77711 6.28273 2.95773 6.10211L3.17575 5.88406C3.35636 5.70344 3.6492 5.70344 3.82982 5.88406L8 10.0543L12.1702 5.88406C12.3508 5.70344 12.6436 5.70344 12.8242 5.88406L13.0423 6.10211C13.2229 6.28273 13.2229 6.57552 13.0423 6.75614L8.32702 11.4714Z"
        fill="currentColor"
      />
    </svg>
  ),
  'carousel-arrow-left': ({ className, strokeWidth }) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path d="M15 8H1" stroke="#333333" strokeWidth={strokeWidth || 2} strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M8 15L1 8L8 1"
        stroke="#333333"
        strokeWidth={strokeWidth || 2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  'carousel-arrow-right': ({ className, strokeWidth }) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path d="M1 8H15" stroke="#333333" strokeWidth={strokeWidth || 2} strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M8 1L15 8L8 15"
        stroke="#333333"
        strokeWidth={strokeWidth || 2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  stars: ({ className }) => (
    <div className={`flex items-center ${className}`}>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 17 16" fill="none">
        <g clip-path="url(#clip0_10509_3137)">
          <path
            fill="#000000"
            d="M8.89678 0L11.5015 5.12582L16.9999 6.1119L13.1081 10.2653L13.9048 16L8.89678 13.4416L3.88872 16L4.68545 10.2653L0.793701 6.1119L6.29644 5.12582L8.89678 0Z"
          />
        </g>
        <defs>
          <clipPath id="clip0_10509_3137">
            <rect width="16.2062" height="16" fill="white" transform="translate(0.793701)" />
          </clipPath>
        </defs>
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 17 16" fill="none">
        <g clip-path="url(#clip0_10509_3137)">
          <path
            fill="#000000"
            d="M8.89678 0L11.5015 5.12582L16.9999 6.1119L13.1081 10.2653L13.9048 16L8.89678 13.4416L3.88872 16L4.68545 10.2653L0.793701 6.1119L6.29644 5.12582L8.89678 0Z"
          />
        </g>
        <defs>
          <clipPath id="clip0_10509_3137">
            <rect width="16.2062" height="16" fill="white" transform="translate(0.793701)" />
          </clipPath>
        </defs>
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 17 16" fill="none">
        <g clip-path="url(#clip0_10509_3137)">
          <path
            fill="#000000"
            d="M8.89678 0L11.5015 5.12582L16.9999 6.1119L13.1081 10.2653L13.9048 16L8.89678 13.4416L3.88872 16L4.68545 10.2653L0.793701 6.1119L6.29644 5.12582L8.89678 0Z"
          />
        </g>
        <defs>
          <clipPath id="clip0_10509_3137">
            <rect width="16.2062" height="16" fill="white" transform="translate(0.793701)" />
          </clipPath>
        </defs>
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 17 16" fill="none">
        <g clip-path="url(#clip0_10509_3137)">
          <path
            fill="#000000"
            d="M8.89678 0L11.5015 5.12582L16.9999 6.1119L13.1081 10.2653L13.9048 16L8.89678 13.4416L3.88872 16L4.68545 10.2653L0.793701 6.1119L6.29644 5.12582L8.89678 0Z"
          />
        </g>
        <defs>
          <clipPath id="clip0_10509_3137">
            <rect width="16.2062" height="16" fill="white" transform="translate(0.793701)" />
          </clipPath>
        </defs>
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 17 16" fill="none">
        <g clip-path="url(#clip0_10509_3137)">
          <path
            fill="#000000"
            d="M8.89678 0L11.5015 5.12582L16.9999 6.1119L13.1081 10.2653L13.9048 16L8.89678 13.4416L3.88872 16L4.68545 10.2653L0.793701 6.1119L6.29644 5.12582L8.89678 0Z"
          />
        </g>
        <defs>
          <clipPath id="clip0_10509_3137">
            <rect width="16.2062" height="16" fill="white" transform="translate(0.793701)" />
          </clipPath>
        </defs>
      </svg>
    </div>
  ),
  'icon-arrow-down': ({ className }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      className={className}
    >
      <path
        d="M8.17674 11.8233L8.17676 11.8233L8.5303 11.4697C8.53031 11.4697 8.53031 11.4697 8.53032 11.4697C8.62794 11.3721 8.78621 11.3721 8.88385 11.4697C8.88385 11.4697 8.88385 11.4697 8.88385 11.4697L15.6464 18.2323L16 18.5859L16.3535 18.2323L23.1161 11.4697C23.2138 11.3721 23.372 11.3721 23.4696 11.4697L23.8232 11.8233C23.9208 11.921 23.9208 12.0792 23.8232 12.1768L16.1767 19.8233C16.0791 19.9209 15.9209 19.9209 15.8232 19.8233L8.17674 12.1768C8.17674 12.1768 8.17674 12.1768 8.17674 12.1768C8.07911 12.0792 8.07911 11.9209 8.17674 11.8233Z"
        fill="black"
      />
    </svg>
  ),
  'share-cart': ({ className, strokeWidth }) => (
    <svg
      className={className}
      width="18"
      height="24"
      viewBox="0 0 18 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13 8.7H15C15.5304 8.7 16.0391 8.91071 16.4142 9.28579C16.7893 9.66086 17 10.1696 17 10.7V20.3C17 20.8304 16.7893 21.3391 16.4142 21.7142C16.0391 22.0893 15.5304 22.3 15 22.3H3C2.46957 22.3 1.96086 22.0893 1.58579 21.7142C1.21071 21.3391 1 20.8304 1 20.3V10.7C1 10.1696 1.21071 9.66086 1.58579 9.28579C1.96086 8.91071 2.46957 8.7 3 8.7H5M13 5.5L9 1.5M9 1.5L5 5.5M9 1.5V15.15"
        stroke="currentColor"
        strokeWidth={strokeWidth || 1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  'icon-nose-opening': () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="101" height="54" viewBox="0 0 101 54" fill="none">
      <g clipPath="url(#clip0_8781_1842)">
        <rect x="0.5" y="19.4319" width="74.2653" height="6.18878" fill="black" />
        <rect x="0.5" y="25.6206" width="35.0697" height="6.18878" fill="black" />
        <rect x="39.6955" y="25.6206" width="35.0697" height="6.18878" fill="black" />
        <rect x="6.68878" y="31.8093" width="22.6922" height="6.18878" fill="black" />
        <rect x="45.8843" y="31.8093" width="22.6922" height="6.18878" fill="black" />
        <rect x="11.8461" y="37.998" width="12.3776" height="6.18878" fill="black" />
        <rect x="51.0417" y="37.998" width="12.3776" height="6.18878" fill="black" />
        <rect x="9.81787" y="25.6206" width="6.18878" height="6.18878" fill="white" />
        <rect x="49.0135" y="25.6206" width="6.18878" height="6.18878" fill="white" />
        <rect x="14.9752" y="31.8093" width="6.18878" height="6.18878" fill="white" />
        <rect x="74.7653" y="13.2429" width="6.18878" height="6.18878" fill="black" />
        <rect x="6.68878" y="13.2429" width="6.18878" height="6.18878" fill="black" />
        <rect x="80.9541" y="7.05396" width="6.18878" height="6.18878" fill="black" />
        <rect x="12.8775" y="7.05396" width="6.18878" height="6.18878" fill="black" />
        <rect x="87.1428" y="0.865234" width="6.18878" height="6.18878" fill="black" />
        <rect x="93.3316" y="7.0542" width="6.18878" height="6.18878" fill="black" />
        <rect x="19.0663" y="0.865234" width="6.18878" height="6.18878" fill="black" />
        <rect x="25.2551" y="7.0542" width="6.18878" height="6.18878" fill="black" />
        <rect x="54.1708" y="31.8093" width="6.18878" height="6.18878" fill="white" />
        <path
          d="M38.3397 37.2909C37.9492 36.9004 37.316 36.9004 36.9255 37.2909L30.5615 43.6549C30.171 44.0454 30.171 44.6786 30.5615 45.0691C30.952 45.4596 31.5852 45.4596 31.9757 45.0691L37.6326 39.4123L43.2894 45.0691C43.6799 45.4596 44.3131 45.4596 44.7036 45.0691C45.0942 44.6786 45.0942 44.0454 44.7036 43.6549L38.3397 37.2909ZM38.6326 53.1345L38.6326 37.998L36.6326 37.998L36.6326 53.1345L38.6326 53.1345Z"
          fill="#FF0000"
        />
      </g>
      <defs>
        <clipPath id="clip0_8781_1842">
          <rect width="100" height="52.2693" fill="white" transform="translate(0.5 0.865234)" />
        </clipPath>
      </defs>
    </svg>
  ),
  'icon-frame-height': () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="101" height="44" viewBox="0 0 101 44" fill="none">
      <g clipPath="url(#clip0_8768_1816)">
        <rect x="0.5" y="18.6077" width="74.2653" height="6.18878" fill="black" />
        <rect x="0.5" y="24.7949" width="35.0697" height="6.18878" fill="black" />
        <rect x="39.6953" y="24.7949" width="35.0697" height="6.18878" fill="black" />
        <rect x="6.68945" y="30.9863" width="22.6922" height="6.18878" fill="black" />
        <rect x="45.8848" y="30.9863" width="22.6922" height="6.18878" fill="black" />
        <rect x="11.8457" y="37.1738" width="12.3776" height="6.18878" fill="black" />
        <rect x="51.041" y="37.1738" width="12.3776" height="6.18878" fill="black" />
        <rect x="9.81836" y="24.7949" width="6.18878" height="6.18878" fill="white" />
        <rect x="49.0137" y="24.7949" width="6.18878" height="6.18878" fill="white" />
        <rect x="14.9746" y="30.9863" width="6.18878" height="6.18878" fill="white" />
        <rect x="74.7656" y="12.4199" width="6.18878" height="6.18878" fill="black" />
        <rect x="6.68945" y="12.4199" width="6.18878" height="6.18878" fill="black" />
        <rect x="80.9531" y="6.22852" width="6.18878" height="6.18878" fill="black" />
        <rect x="12.877" y="6.22852" width="6.18878" height="6.18878" fill="black" />
        <rect x="87.1426" y="0.0410156" width="6.18878" height="6.18878" fill="black" />
        <rect x="93.332" y="6.22852" width="6.18878" height="6.18878" fill="black" />
        <rect x="19.0663" y="0.0410156" width="6.18878" height="6.18878" fill="black" />
        <rect x="25.2559" y="6.22852" width="6.18878" height="6.18878" fill="black" />
        <rect x="54.1699" y="30.9863" width="6.18878" height="6.18878" fill="white" />
        <path
          d="M89.4239 19.6347C89.0334 19.2442 88.4002 19.2442 88.0097 19.6347L81.6457 25.9987C81.2552 26.3892 81.2552 27.0223 81.6457 27.4129C82.0363 27.8034 82.6694 27.8034 83.0599 27.4129L88.7168 21.756L94.3737 27.4129C94.7642 27.8034 95.3973 27.8034 95.7879 27.4129C96.1784 27.0223 96.1784 26.3892 95.7879 25.9987L89.4239 19.6347ZM88.0097 44.6663C88.4002 45.0569 89.0334 45.0569 89.4239 44.6663L95.7879 38.3024C96.1784 37.9118 96.1784 37.2787 95.7879 36.8882C95.3973 36.4976 94.7642 36.4976 94.3737 36.8882L88.7168 42.545L83.0599 36.8882C82.6694 36.4976 82.0363 36.4976 81.6457 36.8882C81.2552 37.2787 81.2552 37.9119 81.6457 38.3024L88.0097 44.6663ZM87.7168 20.3418L87.7168 43.9592L89.7168 43.9592L89.7168 20.3418L87.7168 20.3418Z"
          fill="#FF0000"
        />
      </g>
      <defs>
        <clipPath id="clip0_8768_1816">
          <rect width="100" height="43.9182" fill="white" transform="translate(0.5 0.0410156)" />
        </clipPath>
      </defs>
    </svg>
  ),
  'icon-frame-length': () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="101" height="58" viewBox="0 0 101 58" fill="none">
      <g clipPath="url(#clip0_8768_1872)">
        <rect x="0.5" y="18.5667" width="74.2653" height="6.18878" fill="black" />
        <rect x="0.5" y="24.7554" width="35.0697" height="6.18878" fill="black" />
        <rect x="39.6956" y="24.7554" width="35.0697" height="6.18878" fill="black" />
        <rect x="6.68872" y="30.9441" width="22.6922" height="6.18878" fill="black" />
        <rect x="45.8843" y="30.9441" width="22.6922" height="6.18878" fill="black" />
        <rect x="11.8462" y="37.1328" width="12.3776" height="6.18878" fill="black" />
        <rect x="51.0417" y="37.1328" width="12.3776" height="6.18878" fill="black" />
        <rect x="9.81787" y="24.7554" width="6.18878" height="6.18878" fill="white" />
        <rect x="49.0134" y="24.7554" width="6.18878" height="6.18878" fill="white" />
        <rect x="14.9752" y="30.9441" width="6.18878" height="6.18878" fill="white" />
        <rect x="74.7653" y="12.3777" width="6.18878" height="6.18878" fill="black" />
        <rect x="6.68872" y="12.3777" width="6.18878" height="6.18878" fill="black" />
        <rect x="80.9541" y="6.18872" width="6.18878" height="6.18878" fill="black" />
        <rect x="12.8776" y="6.18872" width="6.18878" height="6.18878" fill="black" />
        <rect x="87.1428" width="6.18878" height="6.18878" fill="black" />
        <rect x="93.3317" y="6.18896" width="6.18878" height="6.18878" fill="black" />
        <rect x="19.0663" width="6.18878" height="6.18878" fill="black" />
        <rect x="25.2551" y="6.18896" width="6.18878" height="6.18878" fill="black" />
        <rect x="54.1708" y="30.9441" width="6.18878" height="6.18878" fill="white" />
        <path
          d="M75.4725 49.9764C75.863 49.5859 75.863 48.9527 75.4725 48.5622L69.1085 42.1982C68.718 41.8077 68.0848 41.8077 67.6943 42.1982C67.3038 42.5887 67.3038 43.2219 67.6943 43.6124L73.3512 49.2693L67.6943 54.9261C67.3038 55.3167 67.3038 55.9498 67.6943 56.3404C68.0848 56.7309 68.718 56.7309 69.1085 56.3404L75.4725 49.9764ZM-0.207108 48.5622C-0.597633 48.9527 -0.597633 49.5859 -0.207108 49.9764L6.15685 56.3404C6.54738 56.7309 7.18054 56.7309 7.57107 56.3404C7.96159 55.9498 7.96159 55.3167 7.57107 54.9261L1.91422 49.2693L7.57107 43.6124C7.96159 43.2219 7.96159 42.5887 7.57107 42.1982C7.18054 41.8077 6.54738 41.8077 6.15685 42.1982L-0.207108 48.5622ZM74.7654 48.2693L0.5 48.2693V50.2693L74.7654 50.2693V48.2693Z"
          fill="#FF0000"
        />
      </g>
      <defs>
        <clipPath id="clip0_8768_1872">
          <rect width="100" height="58" fill="white" transform="translate(0.5)" />
        </clipPath>
      </defs>
    </svg>
  ),
  'icon-temple-length': () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="101" height="46" viewBox="0 0 101 46" fill="none">
      <g clipPath="url(#clip0_8781_1900)">
        <rect x="0.5" y="20.6438" width="74.2653" height="6.18878" fill="black" />
        <rect x="0.5" y="26.8325" width="35.0697" height="6.18878" fill="black" />
        <rect x="39.6956" y="26.8325" width="35.0697" height="6.18878" fill="black" />
        <rect x="6.68872" y="33.0212" width="22.6922" height="6.18878" fill="black" />
        <rect x="45.8843" y="33.0212" width="22.6922" height="6.18878" fill="black" />
        <rect x="11.8462" y="39.21" width="12.3776" height="6.18878" fill="black" />
        <rect x="51.0417" y="39.21" width="12.3776" height="6.18878" fill="black" />
        <rect x="9.81787" y="26.8325" width="6.18878" height="6.18878" fill="white" />
        <rect x="49.0134" y="26.8325" width="6.18878" height="6.18878" fill="white" />
        <rect x="14.9752" y="33.0212" width="6.18878" height="6.18878" fill="white" />
        <rect x="74.7653" y="14.4548" width="6.18878" height="6.18878" fill="black" />
        <rect x="6.68872" y="14.4548" width="6.18878" height="6.18878" fill="black" />
        <rect x="80.9541" y="8.26587" width="6.18878" height="6.18878" fill="black" />
        <rect x="12.8776" y="8.26587" width="6.18878" height="6.18878" fill="black" />
        <rect x="87.1428" y="2.07715" width="6.18878" height="6.18878" fill="black" />
        <rect x="93.3317" y="8.26611" width="6.18878" height="6.18878" fill="black" />
        <rect x="19.0663" y="2.07715" width="6.18878" height="6.18878" fill="black" />
        <rect x="25.2551" y="8.26611" width="6.18878" height="6.18878" fill="black" />
        <rect x="54.1708" y="33.0212" width="6.18878" height="6.18878" fill="white" />
        <path
          d="M59.5195 1.60132C59.5195 1.04903 59.0718 0.601318 58.5195 0.601318L49.5195 0.601318C48.9672 0.601318 48.5195 1.04903 48.5195 1.60132C48.5195 2.1536 48.9672 2.60132 49.5195 2.60132L57.5195 2.60132L57.5195 10.6013C57.5195 11.1536 57.9672 11.6013 58.5195 11.6013C59.0718 11.6013 59.5195 11.1536 59.5195 10.6013L59.5195 1.60132ZM42.5632 16.5576C42.5632 17.1099 43.0109 17.5576 43.5632 17.5576L52.5632 17.5576C53.1155 17.5576 53.5632 17.1099 53.5632 16.5576C53.5632 16.0053 53.1155 15.5576 52.5632 15.5576L44.5632 15.5576L44.5632 7.55762C44.5632 7.00533 44.1155 6.55762 43.5632 6.55762C43.0109 6.55762 42.5632 7.00533 42.5632 7.55762L42.5632 16.5576ZM57.8124 0.894212L42.8561 15.8505L44.2703 17.2647L59.2266 2.30843L57.8124 0.894212Z"
          fill="#FF0000"
        />
      </g>
      <defs>
        <clipPath id="clip0_8781_1900">
          <rect width="100" height="44.7974" fill="white" transform="translate(0.5 0.601318)" />
        </clipPath>
      </defs>
    </svg>
  ),
  'icon-radio-toggle-on': () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <g filter="url(#filter0_diii_10693_2183)">
        <path
          d="M3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10C21 14.9706 16.9706 19 12 19C7.02944 19 3 14.9706 3 10Z"
          fill="#FBFBFB"
        />
        <circle cx="11.6525" cy="10.394" r="4" fill="#FAFAFA" />
      </g>
      <defs>
        <filter
          id="filter0_diii_10693_2183"
          x="0"
          y="-5"
          width="24"
          height="29"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.31 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_10693_2183" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_10693_2183" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="-5" />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.7 0" />
          <feBlend mode="normal" in2="shape" result="effect2_innerShadow_10693_2183" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="4" operator="erode" in="SourceAlpha" result="effect3_innerShadow_10693_2183" />
          <feOffset dy="-6" />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.24786 0 0 0 0 0.621464 0 0 0 0 0.907161 0 0 0 1 0" />
          <feBlend mode="normal" in2="effect2_innerShadow_10693_2183" result="effect3_innerShadow_10693_2183" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect4_innerShadow_10693_2183" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0" />
          <feBlend mode="normal" in2="effect3_innerShadow_10693_2183" result="effect4_innerShadow_10693_2183" />
        </filter>
      </defs>
    </svg>
  ),
  'icon-radio-toggle-off': () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_diii_10693_991)">
        <path
          d="M3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10C21 14.9706 16.9706 19 12 19C7.02944 19 3 14.9706 3 10Z"
          fill="white"
        />
        <path
          d="M3.5 10C3.5 5.30558 7.30558 1.5 12 1.5C16.6944 1.5 20.5 5.30558 20.5 10C20.5 14.6944 16.6944 18.5 12 18.5C7.30558 18.5 3.5 14.6944 3.5 10Z"
          stroke="#AAAAAA"
        />
      </g>
      <defs>
        <filter
          id="filter0_diii_10693_991"
          x="0"
          y="-5"
          width="24"
          height="29"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.31 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_10693_991" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_10693_991" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="-5" />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.7 0" />
          <feBlend mode="normal" in2="shape" result="effect2_innerShadow_10693_991" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="4" operator="erode" in="SourceAlpha" result="effect3_innerShadow_10693_991" />
          <feOffset dy="-6" />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.747152 0 0 0 0 0.747152 0 0 0 0 0.747152 0 0 0 1 0" />
          <feBlend mode="normal" in2="effect2_innerShadow_10693_991" result="effect3_innerShadow_10693_991" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect4_innerShadow_10693_991" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0" />
          <feBlend mode="normal" in2="effect3_innerShadow_10693_991" result="effect4_innerShadow_10693_991" />
        </filter>
      </defs>
    </svg>
  ),
}

const Icon: React.FC<IconProps> = ({ name, className, strokeWidth }) => {
  const SelectedIcon = icons[name]

  if (!SelectedIcon) {
    console.error(`Icon with name "${name}" not found.`)
    return null
  }

  return <SelectedIcon className={className} strokeWidth={strokeWidth} />
}

export default Icon

// Usage example
/**
 * Example usage of the Icon component:
 *
 * ```tsx
 * import Icon from '@components/atoms/Icon';
 *
 * const App: React.FC = () => {
 *   return (
 *     <div>
 *       <h1>Icon Example</h1>
 *       <Icon name="close" className="text-red-500" strokeWidth={2} />
 *       <Icon name="minus" className="text-blue-500" strokeWidth={1.5} />
 *       <Icon name="plus" className="text-green-500" strokeWidth={1} />
 *     </div>
 *   );
 * };
 *
 * export default App;
 * ```
 */
